import { useCallback, useEffect, useMemo, useState } from "react";

import { StatsPeriodFilter } from "services/stats/rest";
import { StatsIntervalDto, StatsPriceDto } from "services/stats/restModels";
import { useConstants } from "shared/hooks/useConstants";
import { useLocale } from "shared/hooks/useLocale";
import { useMenuOptions } from "shared/hooks/useMenuOptions";
import { useStatsSummary } from "shared/hooks/useStatsSummary";
import { useStatsTimeSeries } from "shared/hooks/useStatsTimeSeries";
import { useStatsTopCategories } from "shared/hooks/useStatsTopCategories";
import { useStatsTopItems } from "shared/hooks/useStatsTopItems";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { formatDate } from "shared/lib/dateTime";
import {
    buildStatsCompareRange,
    findStatsPeriodOption,
    StatsPeriodKey,
    StatsRange,
} from "shared/lib/statsPeriods";

import { STATS_TOP_LIMIT } from "features/Statistics/constants";
import { useStatisticsFilters } from "features/Statistics/hooks/useStatisticsFilters";
import { StatisticsState, StatsMetric } from "features/Statistics/types";

export const useStatisticsState = (): StatisticsState => {
    const locale = useLocale();

    const { menuOptions, loading: menusLoading, error: menusError } = useMenuOptions();
    const { constants, loading: constantsLoading, error: constantsError } = useConstants();

    const { value: filters, patch } = useStatisticsFilters();
    const { menuId, period, range, interval } = filters;

    const [chartMetric, setChartMetric] = useState<StatsMetric | null>(null);

    // Меню в кабинете пока выбирается на самом экране (в шапку селект переедет позже) —
    // до первого выбора берём первое меню филиала.
    useEffect(() => {
        if (menuId !== null || menuOptions.length === 0) return;

        patch({ menuId: menuOptions[0].id });
    }, [menuId, menuOptions, patch]);

    const from = range.from.getTime();
    const to = range.to.getTime();

    const filter = useMemo<StatsPeriodFilter | null>(
        () => (menuId === null ? null : { menuId, from, to }),
        [menuId, from, to]
    );

    const compareFilter = useMemo<StatsPeriodFilter | null>(() => {
        if (menuId === null) return null;

        const compare = buildStatsCompareRange(period, { from: new Date(from), to: new Date(to) });

        return { menuId, from: compare.from.getTime(), to: compare.to.getTime() };
    }, [menuId, period, from, to]);

    const { summary, loading: summaryLoading, error: summaryError } = useStatsSummary(
        filter,
        compareFilter
    );

    const {
        categories,
        loading: categoriesLoading,
        error: categoriesError,
    } = useStatsTopCategories(filter, STATS_TOP_LIMIT);

    const { items, loading: itemsLoading, error: itemsError } = useStatsTopItems(
        filter,
        STATS_TOP_LIMIT
    );

    // Ряд графика грузим только при открытой модалке: это ещё одна агрегация по всем заказам
    // периода, а видят её далеко не на каждом заходе.
    const {
        series,
        loading: seriesLoading,
        error: seriesError,
    } = useStatsTimeSeries(chartMetric === null ? null : filter, chartMetric === null ? null : interval);

    const currencies = constants?.currencies;

    const formatAmount = useCallback(
        (amount: number, currencyId: string | null) => {
            const currency = (currencies || []).find((item) => item.id === currencyId) || null;

            return createCurrencyFormatter(currency, locale).toShow(amount);
        },
        [currencies, locale]
    );

    const formatPrice = useCallback(
        (price: StatsPriceDto) => formatAmount(price.price, price.currencyId),
        [formatAmount]
    );

    const setMenuId = useCallback((next: number) => patch({ menuId: next }), [patch]);

    const setPeriod = useCallback((next: StatsPeriodKey) => patch({ period: next }), [patch]);

    const setRange = useCallback(
        (next: StatsRange) => patch({ period: "custom", range: next }),
        [patch]
    );

    const setInterval = useCallback(
        (next: StatsIntervalDto) => patch({ interval: next }),
        [patch]
    );

    const openChart = useCallback((metric: StatsMetric) => setChartMetric(metric), []);

    const closeChart = useCallback(() => setChartMetric(null), []);

    const option = findStatsPeriodOption(period);

    return {
        menuOptions,
        menuId,
        setMenuId,

        period,
        setPeriod,
        range,
        setRange,

        interval,
        setInterval,

        periodLabel:
            period === "custom"
                ? `${formatDate(range.from)} — ${formatDate(range.to)}`
                : option.label,
        compareLabel: option.compareLabel,

        summary,
        categories,
        items,

        // Константы нужны для форматирования сумм — гейтим каркас и на них.
        loading: menusLoading || constantsLoading,
        error: menusError || constantsError,
        dataLoading: summaryLoading || categoriesLoading || itemsLoading,
        dataError: summaryError || categoriesError || itemsError,

        chartMetric,
        openChart,
        closeChart,
        series,
        seriesLoading,
        seriesError,

        locale,
        formatPrice,
        formatAmount,
    };
};
